import React, { useState } from 'react'
import { Box, Flex, Text, HStack, Switch, Grid, GridItem } from '@chakra-ui/react'
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from 'recharts'
import {
  getChartColor,
  getLocationsPercentage,
  getPeriodLabelFromDates,
  getReadableValue,
  isTemperatureParam,
  measurementTypeMap,
  parameterTitleMap,
  scrollBarStyle,
} from '../../../utils/helpers'
import { DashboardResponse } from '../types'
import PercentageChange from '../../reports/pages/components/EnergyReportComponents/PercentageChange'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'

const formatDate = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })

const BAR_WIDTH = 100

interface Props {
  data: DashboardResponse[]
}

interface TooltipProps {
  record: any
  active?: boolean
  payload?: any
  periodLabel?: string
  currStartDate: Date
  currEndDate: Date
  prevStartDate: Date
  prevEndDate: Date
}

const CustomTooltip = ({
  record,
  active,
  payload,
  periodLabel,
  currStartDate,
  currEndDate,
  prevStartDate,
  prevEndDate,
}: TooltipProps) => {
  if (!active || !payload || payload.length < 2) return null

  const [currentBar, prevBar] = payload
  const current = currentBar?.payload
  const prev = prevBar?.payload

  const unit = getReadableValue(measurementTypeMap, current?.parameter_title)

  return (
    <Box bg="gray.700" color="white" p={3} borderRadius="md" boxShadow="md">
      <PercentageChange
        percentageChange={getLocationsPercentage(
          isTemperatureParam(record.parameter_title) ? record.avg : record.total,
          isTemperatureParam(record.parameter_title)
            ? record.prev_avg
            : record.prev_total,
        )}
      />
      <Text fontSize="xs" color="gray.300" mb={2}>
        Compared to previous {periodLabel}
      </Text>

      <Grid templateColumns="auto 1fr" alignItems="center" gap={5}>
        <GridItem>
          <HStack spacing={2}>
            <Text fontWeight={700}>
              {current?.value?.toFixed(2)}{' '}
              <span style={{ fontWeight: 400, fontSize: 11 }}>{unit}</span>
            </Text>
            {unit === 'kWh' && (
              <Text fontWeight={700}>
                {current?.cost?.toFixed(2)}{' '}
                <span style={{ fontWeight: 400, fontSize: 11 }}>лв.</span>
              </Text>
            )}
          </HStack>
        </GridItem>

        <GridItem justifySelf="end">
          <Text fontSize="xs" color="gray.400">
            {periodLabel === 'day'
              ? `${formatDate(currStartDate)}`
              : `${formatDate(currStartDate)}–${formatDate(currEndDate)}`}
          </Text>
        </GridItem>
      </Grid>

      {prev && (
        <Grid templateColumns="auto 1fr" alignItems="center" gap={5}>
          <GridItem>
            <HStack spacing={2}>
              <Text fontWeight={700}>
                {prev?.prev_value?.toFixed(2)}{' '}
                <span style={{ fontWeight: 400, fontSize: 11 }}>{unit}</span>
              </Text>
              {unit === 'kWh' && (
                <Text fontWeight={700}>
                  {prev?.prev_cost?.toFixed(2)}{' '}
                  <span style={{ fontWeight: 400, fontSize: 11 }}>лв.</span>
                </Text>
              )}
            </HStack>
          </GridItem>

          <GridItem justifySelf="end">
            <Text fontSize="xs" color="gray.400">
              {periodLabel === 'day'
                ? `${formatDate(prevStartDate)}`
                : `${formatDate(prevStartDate)}–${formatDate(prevEndDate)}`}
            </Text>
          </GridItem>
        </Grid>
      )}
    </Box>
  )
}

const ChartItem = ({
  record,
  index,
  chartData,
  maxValue,
  periodLabel,
  currStartDate,
  currEndDate,
  prevStartDate,
  prevEndDate,
}: any) => {
  const [showLastWeek, setShowLastWeek] = useState(false)
  const barValueKey = 'value'
  const barPrevValueKey = 'prev_value'

  return (
    <Box key={index} mb={10}>
      <Text fontWeight={700} color="#484848" fontSize="14px" fontFamily="Inter" mb={6}>
        {getReadableValue(parameterTitleMap, record.parameter_title)}
      </Text>

      <Box sx={{ ...scrollBarStyle }} overflowX="auto">
        <Box>
          <ResponsiveContainer width="100%" height={500}>
            <ReBarChart data={chartData} barSize={showLastWeek ? 50 : BAR_WIDTH}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12 }}
                interval={0}
                height={60}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                width={40}
                tickLine={false}
                axisLine={false}
                domain={[0, Math.ceil(maxValue)]}
              />
              {showLastWeek && (
                <Tooltip
                  cursor={false}
                  content={
                    <CustomTooltip
                      periodLabel={periodLabel}
                      record={record}
                      currStartDate={currStartDate}
                      currEndDate={currEndDate}
                      prevStartDate={prevStartDate}
                      prevEndDate={prevEndDate}
                    />
                  }
                />
              )}
              <Bar
                dataKey={barValueKey}
                fill={getChartColor(record.parameter_title)}
                radius={[8, 8, 0, 0]}
                minPointSize={50}
              >
                {!showLastWeek && (
                  <LabelList
                    dataKey={barValueKey}
                    content={({ x, y, width, value }) => {
                      const xPos = Number(x) + Number(width) / 2
                      const fullValue = (value as number)?.toFixed(2)
                      const unit = getReadableValue(
                        measurementTypeMap,
                        record.parameter_title,
                      )
                      const barData = chartData.find((d) => d[barValueKey] === value)
                      const fullCost =
                        barData?.cost !== undefined ? barData.cost?.toFixed(2) : null

                      return value === 0 &&
                        (!fullCost || Number(fullCost) === 0) ? null : (
                        <g>
                          <title>
                            {fullValue} {unit}
                            {fullCost ? ` • ${fullCost} лв.` : ''}
                          </title>
                          <text
                            x={xPos}
                            y={Number(y) + 15}
                            fill="#484848"
                            textAnchor="middle"
                            fontSize="12px"
                            fontWeight="700"
                          >
                            {fullValue} {unit}
                          </text>
                          {fullCost && Number(fullCost) !== 0 && (
                            <text
                              x={xPos}
                              y={Number(y) + 30}
                              fill="#4A5568"
                              textAnchor="middle"
                              fontSize="11"
                            >
                              {fullCost} лв.
                            </text>
                          )}
                        </g>
                      )
                    }}
                  />
                )}
              </Bar>

              {showLastWeek && (
                <Bar
                  dataKey={barPrevValueKey}
                  fill={getChartColor(record.parameter_title)}
                  radius={[8, 8, 0, 0]}
                />
              )}
            </ReBarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {(record.total || record.avg) !== undefined && (
        <>
          <Flex mb="50px" align="center" justify="space-between" fontSize="sm">
            <HStack spacing={2}>
              <Text fontWeight={400} color="#9B9B9B">
                {record.parameter_title !== 'temperature' ? 'Total:' : 'Average:'}
              </Text>
              <Text fontWeight={700} color="#484848">
                {isTemperatureParam(record.parameter_title)
                  ? record.avg?.toFixed(2)
                  : record.total?.toFixed(2)}{' '}
                <span style={{ fontWeight: 400, color: '#9B9B9B' }}>
                  {getReadableValue(measurementTypeMap, record.parameter_title)}
                </span>
              </Text>
              {record.parameter_title !== 'temperature' && (
                <Text fontWeight={700} color="#484848">
                  {record.total_cost?.toFixed(2)}{' '}
                  <span style={{ fontWeight: 400, color: '#9B9B9B' }}>лв.</span>
                </Text>
              )}
              <PercentageChange
                percentageChange={getLocationsPercentage(
                  isTemperatureParam(record.parameter_title) ? record.avg : record.total,
                  isTemperatureParam(record.parameter_title)
                    ? record.prev_avg
                    : record.prev_total,
                )}
              />
            </HStack>
            <HStack>
              <Switch
                size="sm"
                isChecked={showLastWeek}
                onChange={() => setShowLastWeek(!showLastWeek)}
              />
              <Text color="gray.500" fontSize="sm">
                Show last {periodLabel} (
                {periodLabel === 'day'
                  ? `${formatDate(prevStartDate)}`
                  : `${formatDate(prevStartDate)}–${formatDate(prevEndDate)}`}
                )
              </Text>
            </HStack>
          </Flex>
          <Box border="5px solid #DEDFE2" opacity="20%" borderRadius="full" />
        </>
      )}
    </Box>
  )
}

export function BarChart(props: Props) {
  const { data } = props
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')
  const to = searchParams.get('to')

  const periodLabel = getPeriodLabelFromDates(from, to)

  const currStartDate = from ? dayjs(from) : dayjs()
  const currEndDate = to ? dayjs(to) : dayjs()

  const diffInDays = periodLabel === 'day' ? 1 : currEndDate.diff(currStartDate, 'day')

  const prevStartDate = currStartDate.subtract(diffInDays, 'day')

  return (
    <Box width="100%">
      {data.map((record, index) => {
        const chartData = record.values.map((entry) => ({
          ...entry,
          parameter_title: record.parameter_title,
        }))

        const maxValue = Math.max(...chartData.map((entry) => entry.value ?? 0))

        return (
          <ChartItem
            key={index}
            record={record}
            index={index}
            chartData={chartData}
            maxValue={maxValue}
            periodLabel={periodLabel}
            currStartDate={currStartDate.toDate()}
            currEndDate={currEndDate.toDate()}
            prevStartDate={prevStartDate.toDate()}
            prevEndDate={currStartDate.toDate()}
          />
        )
      })}
    </Box>
  )
}
